import React, { createContext, useContext, useState } from "react";
import "./Toast.scss"; // Import SCSS styles
import { IconButton } from "@mui/material";
import { Close, Done } from "@mui/icons-material";

const ToastContext = createContext();

const Toast = ({ message, type = "info", duration = 5000, onClose, action, hideIcon = false, inlineAction = false }) => {
    const [isVisible, setIsVisible] = useState(false); // Initially hidden

    React.useEffect(() => {
        // Delay adding class to trigger animation
        const showTimer = setTimeout(() => setIsVisible(true), 10);

        const hideTimer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(onClose, 300); // Wait for exit animation
        }, duration);

        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [duration, onClose]);

    const handleAction = () => {
        if (action?.onClick) {
            action.onClick();
        }
        onClose();
    };

    return (
        <div className={`toast ${type} ${isVisible ? "animate-in" : ""}`}>
            {/* Slot is dropped entirely (not emptied) so the message fills the space */}
            {!hideIcon && (
                <div className="toast-icon">
                    {type === "success" && <Done />}
                    {/* {type === "success" && "✔"} */}
                    {type === "error" && <Close />}
                    {/* {type === "error" && "✖"} */}
                    {type === "warning" && "⚠"}
                    {type === "info" && "i"}
                </div>
            )}
            <div className={`toast-message${inlineAction ? " toast-message--inline" : ""}`}>
                <span>{message}</span>
                {action && (
                    <a href={action.href} onClick={(e) => {
                        if (action.onClick) {
                            e.preventDefault();
                            handleAction();
                        }
                    }} className="toast-action">
                        {action.label}
                    </a>
                )}
            </div>
            <button className="toast-close" onClick={onClose}>✖</button>
        </div>
    );
};


export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    // options: { hideIcon } - for messages that carry their own icon;
    // { inlineAction } - short message with its action on the same line (e.g. Undo).
    const addToast = (message, type = "info", duration = 5000, action, options = {}) => {
        const newToast = { id: Date.now(), message, type, duration, action, hideIcon: !!options.hideIcon, inlineAction: !!options.inlineAction };
        setToasts((prev) => [...prev, newToast]);

        setTimeout(() => removeToast(newToast.id), duration);
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    const resetToast = () => {
        setToasts([]);
    };

    return (
        <ToastContext.Provider value={{ addToast, resetToast }}>
            {children}
            <div className="toast-container">
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        {...toast}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

// Custom Hook to use Toast
export const useToast = () => useContext(ToastContext);
